// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

// Services
import { DistrictService } from '../services/district.service';
import { CoreService } from '../../../shared/services/core.service';
import { SpinnerService } from '../../../shared/components/spinner/services/spinner.service';

// Models
import { DistrictViewModel } from '../models/district-view.model';
import { AppCleanup } from '../../../shared/models';

// Store
import { DistrictActions } from './district.action';

// Enums
import { StatusType } from '../../../shared/enums';

export interface DistrictStateModel {
	items: DistrictViewModel[];
	loaded: boolean;
}

const defaultState: DistrictStateModel = {
	items: undefined,
	loaded: false,
};

@State<any>({
	name: 'district',
	defaults: defaultState,
})
@Injectable()
export class DistrictState extends AppCleanup {
	constructor(
		private spinnerService: SpinnerService,
		private districtService: DistrictService,
		private coreService: CoreService,
		private translateService: TranslateService
	) {
		super();
	}

	@Action(DistrictActions.GetAll)
	getAll(ctx: StateContext<DistrictStateModel>, action: DistrictActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.districtService
			.getDistricts()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (districts) => {
					ctx.setState({
						items: districts,
						loaded: true,
					});

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.DISTRICT.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(DistrictActions.SetAll)
	setAll(ctx: StateContext<DistrictStateModel>, action: DistrictActions.SetAll) {
		ctx.setState({
			items: action.districts,
			loaded: true,
		});
	}

	@Selector()
	static isLoaded(state: DistrictStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static districts(state: DistrictStateModel): DistrictViewModel[] {
		return state.items;
	}
}
